import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Link as LinkIcon, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

// Bulk-invite batchmates into a group by email, plus a copyable invite link.
export default function BulkInviteDialog({ groupId, groupName, label = "Invite batchmates", variant = "secondary", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const inviteLink = `${window.location.origin}/invite/group/${groupId}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success("Invite link copied — share it anywhere"); }
    catch { toast.error("Could not copy the link"); }
  };

  const send = async () => {
    const count = (raw.match(/[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g) || []).length;
    if (!count) { toast.error("Add at least one email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/invite-emails`, { raw, note });
      toast.success(`Invited ${data.sent} batchmate${data.sent === 1 ? "" : "s"} to ${groupName}`);
      setRaw(""); setNote(""); setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invites");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className="gap-2 rounded-full" data-testid="bulk-invite-open"><UserPlus size={16} weight="duotone" /> {label}</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="bulk-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite your batchmates</DialogTitle>
          <DialogDescription>Paste their emails (any separator works) — we'll email each a link to join {groupName}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-muted/30 p-2">
            <Input readOnly value={inviteLink} className="border-0 bg-transparent text-xs" data-testid="bulk-invite-link" />
            <Button variant="outline" size="sm" className="shrink-0 gap-1.5 rounded-full" onClick={copyLink} data-testid="bulk-invite-copy"><LinkIcon size={14} /> Copy</Button>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Email addresses</label>
            <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={4}
              placeholder="anita@example.com, ravi@example.com&#10;priya@example.com" data-testid="bulk-invite-emails" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Add a personal note (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Hey! Our batch is reconnecting on AlumniPulse — join us!" data-testid="bulk-invite-note" />
          </div>
          <Button className="w-full gap-2 rounded-full" onClick={send} disabled={sending} data-testid="bulk-invite-send">
            <PaperPlaneTilt size={16} weight="fill" /> {sending ? "Sending…" : "Send invites"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
